import { useState } from 'react'
import { Stack, Box } from '@mui/material'
import { TimelineChart, pivot as pivotTimeline, seriesKeysWindowFirst } from './TimelineChart'
import { AbsoluteBarChart, pivot as pivotAbsolute } from './AbsoluteBarChart'
import { WrapperChart } from './WrapperChart'
import { useStore } from '~/state/useStore'
import { useChartKnobsState, type ChartType } from '~/state/useChartKnobsState'
import { useGranularityState } from '~/state/useGranularityState'
import {
  assignGroups,
  fillGaps,
  summariseByUnit,
  applyRunningTotal,
  partition,
  partitionWithRemainder,
  applyCap,
  computePanelAggregates,
  computeSeriesSums,
  computeGrowth,
  computeDomain,
  splitBaseline,
  baselineBoundaryIso,
  type Group,
  type GranularityUnit,
  type SeriesPoint,
} from './dataPipeline'
import {
  aggregateRatioRowsWithComponents,
  ratioPanelSum,
  type RatioComponentsByBucket,
} from './aggregateRatioRows'
import { BaselinePanel } from './BaselinePanel'
import { HiddenRemainderNote } from './HiddenRemainderNote'
import { PanelStatRow, type PanelStat } from './PanelStatRow'
import { TruncatedBanner } from './TruncatedBanner'
import { Knobs } from './Knobs'
import { downgradeFacet } from './facets'
import { ChartLegend } from './ChartLegend'
import { SingleStatTile } from './SingleStatTile'
import { MESSAGES, CHROME } from './chrome/chartChrome'
import { buildFyTickFormatter } from './fyTickFormatter'
import type { AdditivityVerdict } from './seriesAdditivity'
import type { WalkTimelineRow, WalkAbsoluteRow, WalkWrapperRow } from '~/api/endpoints/walks'

const MAX_PANELS = 8
const CAP_INCREMENT = 8

interface Props {
  pinId: string
  chartType: ChartType
  rows: WalkTimelineRow[] | WalkAbsoluteRow[] | WalkWrapperRow[]
  groupBy: string[]
  yLabel: string
  isComparison?: boolean
  /** When provided, overrides the computed Y-domain [min, max] (used for cross-tile Y-sharing
   *  in Columns layout). Contract: both ends must already be nice domain bounds — the Columns
   *  fanout passes computeSharedDomain output (pre-niceCeil'ed on both ends); it is used
   *  verbatim (niceCeil is NOT idempotent, so re-ceiling here would inflate the headroom:
   *  110 → 120 → 150). Task 9: carries the most-negative min across fanout siblings, not just
   *  the max — a sibling with negative bars must not clip every panel to [0, max]. */
  sharedDomain?: [number, number]
  /** value_type -> nice label; passed to assignGroups to prettify multi-value_type series legends. */
  vtLabels?: Record<string, string>
  /** Chart-semantics doctrine (spec 2026-08-03 §1) — REQUIRED: every consumer resolves a real
   *  verdict via useSeriesAdditivity (or resolveSeriesAdditivity) before rendering. No ambient
   *  allow-all — Knobs consumes this directly instead of inferring stackability from `groups`. */
  additivity: AdditivityVerdict
  /** base -> display label for series legends and absolute x-ticks (render-only; keys stay raw). */
  seriesBaseLabelFor?: (base: string) => string
  /** chart_registry.default_facet (raw) — the initial facet when the user hasn't chosen one. */
  defaultFacet?: string | null
  /** #11: true for ratio walks (walk_ratio). Branches the timeline pipeline to aggregate via
   *  Σnumerator/Σdenominator rather than summing the ratio value directly. */
  isRatio?: boolean
  /** Resolved dots from page state (Review pages). Window-less fallback = hook default (pins/Home). */
  dots?: number
  /** Row 15-light: in-window start; points before its bucket boundary form the previous-period
   *  baseline panel. When set on a timeline chart the rows are the EXTENDED walk result; the
   *  leading grayed baseline panel seeds the Δ chain. Pins/compare/absolute never set it. */
  windowFromIso?: string | null
  /** Unit for the baseline boundary split — pass the page's GOVERNED unit (the walk buckets at
   *  that unit even when the requested gran unit is finer). Defaults to the hook unit. */
  baselineUnit?: GranularityUnit
  /** base -> catalog-slot hex (spec D3); passed through to assignGroups. Callers derive this via
   *  useDimValueColorForBase (single group-by dim only) — ChartCanvas itself stays fact-agnostic. */
  colorForBase?: (base: string) => string | undefined
  /** T5: the FULL selected window bounds — distinct from windowFromIso above (which only carries
   *  the previous-period-baseline start, and only when that panel is active). Used solely to
   *  detect a partial fiscal-year bucket at the timeline's edges when the active unit is 'fy'.
   *  Omit on non-timeline callers / pin replays that don't need the FY honesty marker. */
  queryFromIso?: string
  queryToIso?: string
  /** T5 fix round 1: the TRUE extended lower bound the walk was fired with when the
   *  previous-period baseline panel is active (state.panelBaseline.baselineFromIso) — NOT the
   *  same value as windowFromIso above (which carries the split-boundary date, state.fromIso).
   *  Final-review I1: it is the lower bound of the FETCHED window, so it governs the FY partial
   *  marker for EVERY panel here (baseline and in-window alike), not just the baseline panel —
   *  see the fyTickFormatter comment below. */
  baselineFromIso?: string
  /** FY v2 (pin contract, spec §3.3): the DISPLAY unit to fold/label at. Pin replays pass
   *  replayUnitOf(pin) so the viewer's URL granularity never affects a pin. Absent on live
   *  Review pages → the ambient useGranularityState() unit governs, exactly as before. */
  unit?: GranularityUnit
}

/** #11 Exported pure helper — branches the timeline coarsening by isRatio so the logic is
 *  independently testable without needing a full component render.
 *
 *  - ratio:  aggregateRatioRowsWithComponents(rows, unit, runningTotal) → pivot (already coarsened;
 *            skip summarise). The components sidecar rides along so the caller can compute
 *            panel Σ as Σnum/Σdenom (window ratio) rather than naïvely summing bucket ratios.
 *  - volume: pivot → fillGaps → summariseByUnit, then applyRunningTotal when on.
 *            ratioComponents is null for the volume branch.
 *
 *  FU-g: `prePoints` = the pre-running-total aggregation source; volume Σ/ø chips ALWAYS read
 *  these — the running total is a DISPLAY transform and must never change aggregates. With the
 *  knob off (and always on the ratio branch, whose Σ comes from the components sidecar)
 *  prePoints === points by identity.
 *
 * `groups` must be derived from the pivoted seriesKeys BEFORE calling this helper
 * (they're needed for fillGaps/summarise/applyRunningTotal). Callers that test this
 * function directly can build minimal Group objects. */
export function buildTimelinePoints(
  rows: WalkTimelineRow[],
  groups: Group[],
  opts: { unit: GranularityUnit; isRatio: boolean; runningTotal: boolean },
): {
  points: SeriesPoint[]
  prePoints: SeriesPoint[]
  ratioComponents: RatioComponentsByBucket | null
} {
  const { unit, isRatio, runningTotal } = opts
  if (isRatio) {
    // Ratio branch: coarsen (and optionally cumulate) at the ROW level via Σnum/Σdenom,
    // then pivot. summariseByUnit is intentionally SKIPPED — rows are already bucketed.
    // Components are always non-cumulative so panel Σ uses raw-window parts even when
    // the running-total knob is on (ratio chips never read prePoints — identity is fine).
    const agg = aggregateRatioRowsWithComponents(rows, unit, runningTotal)
    const piv = pivotTimeline(agg.rows)
    const points = fillGaps(piv.points, groups)
    return { points, prePoints: points, ratioComponents: agg.components }
  }
  // Volume branch: unchanged pipeline + optional running-total step.
  const piv = pivotTimeline(rows)
  const gapped = fillGaps(piv.points, groups)
  const summarised = summariseByUnit(gapped, unit, groups)
  const points = runningTotal ? applyRunningTotal(summarised, groups) : summarised
  return { points, prePoints: summarised, ratioComponents: null }
}

export function ChartCanvas({
  pinId,
  chartType,
  rows,
  groupBy: _groupBy,
  yLabel,
  isComparison = false,
  sharedDomain,
  vtLabels = {},
  additivity,
  seriesBaseLabelFor,
  defaultFacet,
  isRatio = false,
  dots: dotsProp,
  windowFromIso = null,
  baselineUnit,
  colorForBase,
  queryFromIso,
  queryToIso,
  baselineFromIso,
  unit: unitProp,
}: Props) {
  const palette = useStore((s) => s.personal.chipPalette)
  const knobs = useChartKnobsState(pinId, chartType, defaultFacet)
  const { unit: hookUnit, dots: hookDots } = useGranularityState()
  const unit = unitProp ?? hookUnit
  const dots = dotsProp ?? hookDots
  const [effectiveCap, setEffectiveCap] = useState<number>(MAX_PANELS)

  // T5: FY-aware x-axis tick formatter, built against the window the walk ACTUALLY FETCHED —
  // baselineFromIso when the previous-period panel is on (that is the true extended lower bound
  // the rows came from), queryFromIso otherwise.
  // T6: the builder itself lives in ./fyTickFormatter so DynamicsPanelsTile reuses it.
  //
  // FINAL-REVIEW I1: ONE formatter now serves BOTH the baseline panel and the in-window panels.
  // It used to be two, with the in-window one bound to queryFromIso — but under a baseline the
  // rows are extended and the split boundary is fiscalYearStart(windowFromIso), so the leading
  // in-window bucket holds a COMPLETE fiscal year and was nonetheless marked partial: the exact
  // inverse of what this marker is for, and a silent change of meaning on one checkbox click.
  // Both panel kinds are slices of one contiguous fetch, so one window governs both and they can
  // no longer disagree about the same bucket.
  //
  // Unit read (T6 audit): this is the RAW url unit (useGranularityState() with no timeframe), not
  // state.governance.effectiveUnit — correct, and identical under FY: governance never coarsens
  // TO or AWAY FROM 'fy' (it is off the LADDER and computeEffectiveBucket returns it verbatim),
  // so requested 'fy' ⇔ effective 'fy'. The fold below and the walk's bucket grain agree.
  // Built only when the active unit is 'fy' AND the caller supplied the query window bounds —
  // every other unit/caller gets `undefined`, so TimelineChart falls back to its default
  // shortDateTick (byte-identical rendering for day/week/month/quarter/year).
  const fyTickFormatter =
    unit === 'fy' && queryFromIso != null && queryToIso != null
      ? buildFyTickFormatter(baselineFromIso ?? queryFromIso, queryToIso)
      : undefined

  if (chartType === 'wrapper') {
    return (
      <Box>
        <WrapperChart rows={rows as WalkWrapperRow[]} />
      </Box>
    )
  }

  // Row 15-light: previous-period baseline mode is timeline-only; the prop is ignored for absolute_bar/wrapper.
  const baselineActive = chartType === 'timeline' && windowFromIso != null
  let baselinePoints: SeriesPoint[] = []

  let points: SeriesPoint[] = []
  // FU-g: pre-running-total aggregation source for the volume Σ/ø chips (=== points whenever
  // no running total applies). UNSPLIT even in baseline mode — see preForTimes below.
  let prePoints: SeriesPoint[] = []
  let groups: Group[] = []
  let timeBased = true
  let ratioComponents: RatioComponentsByBucket | null = null
  if (chartType === 'timeline') {
    // Derive groups from the raw pivot's seriesKeys (same as before — the pivot itself is unchanged).
    const piv = pivotTimeline(rows as WalkTimelineRow[])
    // Palette stability (window integrity): with a baseline, re-order the keys so in-window
    // series keep the exact palette slots they'd have with the flag off (see helper docs).
    const seriesKeys = baselineActive
      ? seriesKeysWindowFirst(
          rows as WalkTimelineRow[],
          baselineBoundaryIso(windowFromIso, baselineUnit ?? unit),
        )
      : piv.seriesKeys
    groups = assignGroups(seriesKeys, palette, vtLabels, seriesBaseLabelFor, colorForBase)
    // #11: branch on isRatio to use the ratio-correct coarsening pipeline.
    // #19: destructure components sidecar for ratio-correct panel Σ chips.
    const built = buildTimelinePoints(rows as WalkTimelineRow[], groups, {
      unit,
      isRatio,
      runningTotal: knobs.runningTotal,
    })
    points = built.points
    prePoints = built.prePoints
    ratioComponents = built.ratioComponents
    if (baselineActive) {
      // Split the EXTENDED points at the window-from's own bucket boundary; the boundary
      // bucket stays in-window (identical membership to the no-baseline rendering).
      // Deliberate (row 15-light): with the previous-period baseline on, the running total CONTINUES
      // from the baseline segment — the cumulative line includes the pre-window context. Export, pin, and
      // totals remain strictly in-window. The former combo limit is FIXED (FU-g): Σ/ø chips
      // aggregate from prePoints, so neither the cumulation nor the baseline offset ever leaks
      // into the aggregates (ratio chips were already immune — components are non-cumulative).
      const split = splitBaseline(points, windowFromIso, baselineUnit ?? unit)
      baselinePoints = split.baseline
      points = split.inWindow
    }
  } else {
    // absolute_bar
    const piv = pivotAbsolute(rows as WalkAbsoluteRow[])
    groups = assignGroups(piv.seriesKeys, palette, vtLabels, seriesBaseLabelFor, colorForBase)
    timeBased = false
    points = piv.points.map((p) => ({ time: p.dim, ...p })) as SeriesPoint[]
    prePoints = points // no running total on absolute_bar — chips read the displayed values
  }

  // Wave-3 §3/D4 (JL): a 1-category × 1-series absolute result is a NUMBER, not geometry
  // (e.g. Marketing Invests' bottom "GreenCrate" summary). The guard is DATA-driven and reads
  // the FULL (pre-legend-toggle) `groups` — never `knobs.inactiveSeries`-filtered — so toggling
  // a series off/on can never morph a chart into a tile (or vice versa). Review KPIs' 1×3
  // bottom summary has groups.length===3 and stays a bar chart. Knobs/ChartLegend/PanelStatRow
  // are all suppressed here — they would restate the same single number.
  // Invariant (W3 gate-fix, item E): baselineActive is timeline-only (`chartType === 'timeline'
  // && windowFromIso != null`, computed above) — this absolute_bar-only branch can therefore
  // never fire alongside an active baseline panel.
  if (chartType === 'absolute_bar' && points.length === 1 && groups.length === 1) {
    // W3 gate-fix (item C, no-silent-fallbacks): a missing single value (reachable via a
    // zero-denominator 1×1 ratio window, walk_ratio value=NULL) must NOT silently render as
    // 0 — fall through to the normal chart path below, whose own empty/no-data handling applies.
    const rawValue = points[0]![groups[0]!.key]
    if (rawValue !== null && rawValue !== undefined) {
      // W3 gate-fix (item A): an ungrouped walk's single point carries an EMPTY category label
      // (dims: {} → pivoted dim key ''), which produced an orphaned leading separator ("⸰ Net
      // Spend", live defect on the Marketing Invests bottom tile) when joined unconditionally.
      // Only join with the separator when the category label is non-empty after trim —
      // otherwise the tile label is just the series label.
      const xLabel = seriesBaseLabelFor?.(String(points[0]!.dim)) ?? String(points[0]!.dim)
      const seriesLabel = groups[0]!.label
      const label = xLabel.trim() ? `${xLabel}${CHROME.valueSeparator}${seriesLabel}` : seriesLabel
      return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SingleStatTile value={Number(rawValue)} label={label} />
        </Box>
      )
    }
  }

  // FU-g: time-keyed lookup into the pre-running-total points. splitBaseline above runs on the
  // POST-RT display points while prePoints stays the UNSPLIT full array — keying by bucket time
  // resolves both cleanly (baseline times and in-window panel times alike).
  const preByTime = new Map(prePoints.map((p) => [String(p.time), p]))
  const preForTimes = (times: string[]): SeriesPoint[] =>
    times.map((t) => preByTime.get(t)).filter((p): p is SeriesPoint => p !== undefined)

  // Timeline points are already fully coarsened by buildTimelinePoints; only absolute_bar
  // (timeBased=true but chartType!='timeline') goes through summariseByUnit here.
  const summarised =
    timeBased && chartType !== 'timeline' ? summariseByUnit(points, unit, groups) : points
  // Row 18 policy is temporal-only: absolute_bar panels chunk CATEGORIES, where hiding a
  // trailing chunk would drop real bars (not a windowing artifact) — keep plain partition there.
  const { panels: allPanels, hidden } =
    chartType === 'timeline'
      ? partitionWithRemainder(summarised, dots)
      : { panels: partition(summarised, dots), hidden: [] }
  // applyCap caps the IN-WINDOW panels only — the baseline panel is context and ALWAYS
  // renders (outside the cap count); TruncatedBanner numbers stay in-window-referenced.
  const { displayed, naturalCount, isTruncated } = applyCap(allPanels, effectiveCap)

  // ── Row 15-light baseline aggregates ──────────────────────────────────────────
  // Σ follows the same family-aware rules as the in-window panels: ratio = Σnum/Σdenom
  // via ratioPanelSum (the components COVER the baseline bucket times because the
  // EXTENDED rows flowed into buildTimelinePoints above); volume = plain value sum
  // over the pre-running-total points (FU-g).
  const baselineHasData = baselineActive && baselinePoints.length > 0
  const baselineTimes = baselinePoints.map((p) => String(p.time))
  const baselineAgg = computePanelAggregates(preForTimes(baselineTimes), groups)
  const baselineSum = ratioComponents
    ? ratioPanelSum(baselineTimes, ratioComponents)
    : baselineAgg.sum
  // Δ seed: null when there is no pre-window data (or a zero-denominator ratio baseline) —
  // the first in-window panel then keeps growth null instead of a spurious 0%-based Δ.
  const baselineSeed = baselineHasData ? baselineSum : null

  // Task 4 (chart-semantics): display-time downgrade + Show-Percentage legality — computed from
  // the resolved additivity verdict, NOT written back into knob state (spec §7 pins). A stacked
  // facet requested by URL/pin state on a non-additive selection renders as its non-stacked
  // sibling; Show-Percentage additionally needs an all-non-negative selection (a per-row
  // percent-of-total is meaningless with mixed signs — also (Task 8) drives the zero line).
  const effectiveFacet = downgradeFacet(knobs.facet, additivity.additive)
  const hasNegativeValues = [...(baselineActive ? baselinePoints : []), ...summarised].some((row) =>
    groups.some((g) => {
      const v = row[g.key]
      return v !== undefined && Number(v) < 0
    }),
  )
  const effectiveShowPercentage =
    chartType === 'absolute_bar' &&
    knobs.showPercentage &&
    additivity.additive &&
    !hasNegativeValues

  // Show-Percentage on absolute_bar normalises each row to sum=100% inside AbsoluteBarChart.
  // When that mode is active the YAxis must show 0..100, not the raw-value domain.
  // sharedDomain (from cross-tile Y-sharing in Columns layout) overrides the computed domain
  // but is itself overridden by the percentage-mode normalization (percentage is always 0-100).
  const yDomain: [number, number] = effectiveShowPercentage
    ? [0, 100]
    : sharedDomain !== undefined
      ? sharedDomain // already nice on both ends (see prop contract) — re-ceiling would double-inflate
      : computeDomain(
          baselineHasData ? [baselinePoints, ...displayed] : displayed,
          groups,
          knobs.cumulative,
        )

  if (displayed.length === 0 && !baselineHasData) {
    if (chartType === 'timeline') {
      return (
        <TimelineChart
          points={[]}
          groups={groups}
          facet={effectiveFacet}
          tickFormatter={fyTickFormatter}
        />
      )
    }
    return (
      <AbsoluteBarChart
        points={[]}
        groups={groups}
        facet={effectiveFacet}
        isPercentage={effectiveShowPercentage}
        xTickFormatter={seriesBaseLabelFor}
        colorForCategory={colorForBase}
      />
    )
  }

  // Task 5 (chart-semantics wave), widened by Task 14 (JL wave-close decision, 2026-08-03): ANY
  // non-blessed verdict — vt-heterogeneous, dim-rule, or metadata-pending — has no honest
  // cross-series total to fabricate (spec §1/§7); the Σ row switches to one labeled Σ per
  // series instead of the aggregate Σ/ø/Δ. Ratio charts are excluded: a per-series ratio Σ
  // isn't Σnum/Σdenom-correct without its own per-series components sidecar (out of scope
  // here) — ratio Σ stays the single ratioPanelSum path via the `!ratioComponents` guard.
  const perSeriesMode = !additivity.additive && !ratioComponents

  // Per-series baseline Σs (shared by the baseline slot below AND panel-index-0's Δ seed) —
  // computed once here so both consumers read the identical sums.
  const baselineSeriesSums =
    perSeriesMode && baselineHasData ? computeSeriesSums(preForTimes(baselineTimes), groups) : null

  // ── Panel stat row (D3): Σ/ø/Δ chips hoisted out of the plot overlay into a panel-aligned
  // row rendered ABOVE the <Stack> of panels. Column count matches the panel Stack exactly:
  // the baseline slot (when active) is cell 0, then one cell per displayed panel — same math
  // as before, just computed ahead of the JSX instead of inline in the panel map.
  const panelStats: PanelStat[] = displayed.map((panel, index) => {
    const panelTimes = panel.map((p) => String(p.time))
    if (perSeriesMode) {
      // Task 5: per-series Σ — one sum per group (VT series), each with its own Δ chained
      // from the previous panel's (or the baseline's) per-series sum. No aggregate sum/avg.
      const sums = computeSeriesSums(preForTimes(panelTimes), groups)
      const prevTimes = index > 0 ? (displayed[index - 1] ?? []).map((p) => String(p.time)) : []
      const prevSums =
        index > 0 ? computeSeriesSums(preForTimes(prevTimes), groups) : baselineSeriesSums
      return {
        prefix: yLabel,
        sum: 0,
        avg: 0,
        growth: null,
        perSeries: groups.map((g) => {
          const sum = sums.get(g.key) ?? 0
          const prev = prevSums?.get(g.key)
          return {
            label: g.label,
            sum,
            growth: prev == null || prev === 0 ? null : computeGrowth(prev, sum),
          }
        }),
      }
    }
    // #19: for ratio timeline charts, Σ chip = Σnum/Σdenom (window ratio) via
    // ratioPanelSum; volume path (FU-g): aggregate the panel's bucket times against
    // the PRE-running-total points — the running total is display-only.
    const { sum: volumeSum, avg } = computePanelAggregates(preForTimes(panelTimes), groups)
    const sum = ratioComponents ? (ratioPanelSum(panelTimes, ratioComponents) ?? 0) : volumeSum
    // Row 15-light: ratioComponents cover the baseline bucket times (the EXTENDED rows
    // flow into buildTimelinePoints), so baselineSeed above is the family-correct Σ and
    // panel index 0 seeds its Δ from it instead of staying null. Later panels chain from
    // the previous panel's family-correct Σ, computed only on the index > 0 branch —
    // panel 0 never reads it, so no aggregate work runs there.
    const prevTimes = index > 0 ? (displayed[index - 1] ?? []).map((p) => String(p.time)) : []
    const growth =
      index === 0
        ? baselineSeed !== null
          ? computeGrowth(baselineSeed, sum)
          : null
        : computeGrowth(
            ratioComponents
              ? (ratioPanelSum(prevTimes, ratioComponents) ?? 0)
              : computePanelAggregates(preForTimes(prevTimes), groups).sum,
            sum,
          )
    return { prefix: yLabel, sum, avg, growth }
  })
  // Baseline slot IS cell 0 in row form (no more index+1 shift) — PanelStatRow's own row
  // position drives the chip logic (comparison-chart / Δ-suppression), matching the shifted
  // `index` ControlLabel used to receive.
  const stats: PanelStat[] = baselineActive
    ? [
        {
          prefix: MESSAGES.previousPeriod,
          sum: baselineSum ?? 0,
          avg: baselineAgg.avg,
          growth: null,
          baseline: true,
          empty: !baselineHasData,
          // Task 5: the baseline slot carries its own per-series sums in per-series mode —
          // growth stays null (the baseline IS the Δ reference; nothing precedes it).
          ...(baselineSeriesSums
            ? {
                perSeries: groups.map((g) => ({
                  label: g.label,
                  sum: baselineSeriesSums.get(g.key) ?? 0,
                  growth: null,
                })),
              }
            : {}),
        },
        ...panelStats,
      ]
    : panelStats

  return (
    <Stack spacing={1}>
      {isTruncated ? (
        <TruncatedBanner
          natural={naturalCount}
          effectiveCap={effectiveCap}
          defaultCap={MAX_PANELS}
          onIncreaseCap={() => setEffectiveCap((c) => c + CAP_INCREMENT)}
          onResetCap={() => setEffectiveCap(MAX_PANELS)}
        />
      ) : null}

      <PanelStatRow stats={stats} isComparison={isComparison} leadOffset />

      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
        {baselineActive ? (
          <BaselinePanel
            hasData={baselineHasData}
            points={baselinePoints}
            groups={groups}
            facet={effectiveFacet}
            inactiveSeries={knobs.inactiveSeries}
            yDomain={yDomain}
            tickFormatter={fyTickFormatter}
          />
        ) : null}
        {displayed.map((panel, index) => {
          return (
            <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
              {chartType === 'timeline' ? (
                <TimelineChart
                  points={panel}
                  groups={groups}
                  facet={effectiveFacet}
                  inactiveSeries={knobs.inactiveSeries}
                  yDomain={yDomain}
                  hideYAxis={baselineHasData || index !== 0}
                  tickFormatter={fyTickFormatter}
                />
              ) : (
                <AbsoluteBarChart
                  // Cast: SeriesPoint has `time` while BarPoint expects `dim`. The
                  // pivot mapping above keeps both fields, so this is structurally
                  // safe. Flagged for follow-up cleanup (BarPoint/SeriesPoint unify).
                  points={panel as never}
                  groups={groups}
                  facet={effectiveFacet}
                  inactiveSeries={knobs.inactiveSeries}
                  isPercentage={effectiveShowPercentage}
                  yDomain={yDomain}
                  hideYAxis={index !== 0}
                  xTickFormatter={seriesBaseLabelFor}
                  colorForCategory={colorForBase}
                />
              )}
            </Box>
          )
        })}
      </Stack>

      <HiddenRemainderNote count={hidden.length} />

      <Stack direction="row" spacing={1} alignItems="center">
        <Knobs
          pinId={pinId}
          chartType={chartType}
          defaultFacet={defaultFacet}
          additivity={additivity}
          hasNegativeValues={hasNegativeValues}
        />
        <ChartLegend
          groups={groups}
          inactiveSeries={knobs.inactiveSeries}
          onChange={knobs.setInactiveSeries}
        />
      </Stack>
    </Stack>
  )
}
